import { Plus, AlignLeft, Heading2, MessageSquare, Image, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ContentBlockType } from "@/types/helpArticle";

interface BlockTypeSelectorProps {
  onSelectBlockType: (type: ContentBlockType) => void;
}

const blockTypes: { type: ContentBlockType; label: string; icon: React.ElementType; description: string }[] = [
  {
    type: "paragraph",
    label: "Paragraph",
    icon: AlignLeft,
    description: "Add a text paragraph",
  },
  {
    type: "heading",
    label: "Heading",
    icon: Heading2,
    description: "Add a section heading (H2)",
  },
  {
    type: "callout",
    label: "Callout",
    icon: MessageSquare,
    description: "Add a highlighted callout box",
  },
  {
    type: "image",
    label: "Image",
    icon: Image,
    description: "Add an image with caption",
  },
  {
    type: "video",
    label: "Video",
    icon: Video,
    description: "Embed a YouTube or Vimeo video",
  },
];

export function BlockTypeSelector({ onSelectBlockType }: BlockTypeSelectorProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full border-dashed gap-2">
          <Plus className="h-4 w-4" />
          Add Block
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-56 bg-popover z-50">
        {blockTypes.map(({ type, label, icon: Icon, description }) => (
          <DropdownMenuItem
            key={type}
            onClick={() => onSelectBlockType(type)}
            className="flex items-start gap-3 p-3 cursor-pointer"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background">
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">{label}</span>
              <span className="text-xs text-muted-foreground">{description}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
